import { STATUS_COLETADA } from '../../../meta'

/**
 * Subconjunto do estado do formulário necessário para validar. Tipado de forma
 * estrutural para a função continuar pura e testável fora do React.
 */
export interface CamposParaValidar {
  data_da_os?: string
  propriedade_id?: number | null
  talhao_id?: number | null
  laboratorio_id?: number | null
  safra_consultoria_id?: number | null
  responsaveis_selecionados?: unknown[]
  coleta_liberada?: string
  coletada_em?: string
  status_atual_id?: number | null
  representante_selecionado_id?: number | null
}

export interface ContextoValidacao {
  /** Modo editar: habilita as regras que só valem para OS já criada. */
  isEdicao: boolean
  /** Cliente do tipo 'G' (grupo) exige representante legal. */
  ehGrupo: boolean
  /** A OS precisa de ao menos uma análise para o backend aceitar. */
  possuiAnalises: boolean
}

/** Campos que podem exibir erro, na ordem em que aparecem na tela. */
export type CampoComErro =
  | 'representante'
  | 'data_da_os'
  | 'propriedade'
  | 'talhao'
  | 'laboratorio'
  | 'safra_consultoria'
  | 'responsaveis'
  | 'coleta_liberada'
  | 'coletada_em'
  | 'analises'

/** Mensagem de erro por campo. Ausência de chave = campo válido. */
export type ErrosFormulario = Partial<Record<CampoComErro, string>>

/**
 * Valida os campos obrigatórios antes de salvar.
 *
 * Existe porque o `obrigatorio` dos componentes é apenas o asterisco visual: sem
 * esta checagem a OS é enviada incompleta e o erro só aparece como recusa do
 * backend. Espelha as validações da versão em produção
 * (`ordemLaboratorio/index.tsx:382-401` e `:716-720`), que lá estavam espalhadas
 * entre "adicionar análise" e "salvar".
 *
 * Retorna um mapa campo -> mensagem, consumido pelas props `error`/`errorMessage`
 * de cada componente. As chaves são inseridas na ordem visual dos campos, então
 * a primeira também serve para um aviso geral.
 */
export function validarFormularioOs(
  campos: CamposParaValidar,
  { isEdicao, ehGrupo, possuiAnalises }: ContextoValidacao
): ErrosFormulario {
  const erros: ErrosFormulario = {}

  if (ehGrupo && !campos.representante_selecionado_id) {
    erros.representante = 'Selecione um representante legal'
  }

  if (!campos.data_da_os) {
    erros.data_da_os = 'Informe a data da O.S'
  }

  // Propriedade e talhão são IMUTÁVEIS ao editar: a malha de pontos e o contorno
  // já gravados pertencem a eles, e os campos ficam desabilitados na tela.
  // Validá-los aqui tornaria a OS impossível de salvar sempre que o backend não
  // devolvesse o cod na rota de edição — o usuário via "Selecione o talhão" num
  // campo que ele não tem como preencher.
  if (!isEdicao) {
    if (!campos.propriedade_id) {
      erros.propriedade = 'Selecione a propriedade'
    }

    if (!campos.talhao_id) {
      erros.talhao = 'Selecione o talhão'
    }
  }

  if (!campos.laboratorio_id) {
    erros.laboratorio = 'Selecione o laboratório'
  }

  if (!campos.safra_consultoria_id) {
    erros.safra_consultoria = 'Selecione a safra consultoria'
  }

  if ((campos.responsaveis_selecionados?.length ?? 0) === 0) {
    erros.responsaveis = 'Selecione ao menos um responsável'
  }

  if (!campos.coleta_liberada) {
    erros.coleta_liberada = 'Informe a data de liberação'
  }

  // "Coletado em" só é editável (e exigível) quando a OS está em COLETADA.
  if (isEdicao && campos.status_atual_id === STATUS_COLETADA && !campos.coletada_em) {
    erros.coletada_em = 'Informe a data da coleta'
  }

  // A metade "Análises" não tem campo próprio para exibir erro — vira alerta.
  if (!possuiAnalises) {
    erros.analises = 'Adicione ao menos uma análise'
  }

  return erros
}

/** `true` quando não há nenhuma pendência. */
export function formularioValido(erros: ErrosFormulario): boolean {
  return Object.keys(erros).length === 0
}
